import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { SocialAuthButtons } from './SocialAuthButtons';
import { PhoneLoginModal } from './PhoneLoginModal';
import { AuthBackground } from './auth/AuthBackground';
import { BrandHero } from './auth/BrandHero';
import { useAuthStore } from '../../store/authStore';
import { Eye, EyeOff, Loader2, Smartphone } from 'lucide-react';

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showPhoneLoginModal, setShowPhoneLoginModal] = useState(false);

  const { login, isLoading, error, clearError } = useAuthStore();

  const fromLocation = (location.state as any)?.from;
  const from = fromLocation ? (fromLocation.pathname + (fromLocation.search || '')) : '/app/map';

  const validateForm = (): boolean => {
    const value = identifier.trim();
    if (!value) {
      setValidationError('Email or phone number is required');
      return false;
    }

    if (value.includes('@')) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setValidationError('Please enter a valid email');
        return false;
      }
    } else {
      const digits = value.replace(/\D/g, '');
      if (!/^[6-9]\d{9}$/.test(digits)) {
        setValidationError('Please enter a valid email or 10-digit phone number');
        return false;
      }
    }

    if (!password) {
      setValidationError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    let loginIdentifier = identifier.trim();
    if (!loginIdentifier.includes('@')) {
      loginIdentifier = loginIdentifier.replace(/\D/g, '');
    }

    try {
      await login({ identifier: loginIdentifier, password });
      navigate(from, { replace: true });
    } catch {
      // Error is handled by the store
    }
  };

  const displayError = validationError || error;

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden">
      <AuthBackground />

      {/* Hero Zone */}
      <div
        className="relative z-10 flex-shrink-0 px-6 pb-6 flex flex-col items-center"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 2.5rem)' }}
      >
        <BrandHero />

      </div>

      {/* Form Card — slides up like a bottom sheet */}
      <motion.div
        className="relative z-10 bg-background rounded-t-3xl shadow-xl border-t border-border/30 px-6 pt-6 pb-6 flex flex-col"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', damping: 25, stiffness: 200 }}
      >
        {/* Social Authentication */}
        <div className="flex flex-col items-center">
          <SocialAuthButtons redirectTo={from} />
        </div>

        {/* Divider */}
        <div className="relative w-full max-w-sm mx-auto my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or sign in with email</span>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm mx-auto space-y-4 flex-1">
          {/* Identifier Field */}
          <div className="space-y-2">
            <Label htmlFor="identifier">Email or Phone Number</Label>
            <Input
              id="identifier"
              type="text"
              placeholder="Enter email or phone number"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setValidationError(null);
                clearError();
              }}
              autoComplete="username"
              // Stays type="text" because this field also accepts phone numbers,
              // which means iOS applies its default sentence-case autocapitalisation
              // and turns "sethu@…" into "Sethu@…". Login is case-sensitive on the
              // server, so without this the first sign-in attempt always 401s.
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={isLoading}
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setValidationError(null);
                  clearError();
                }}
                autoComplete="current-password"
                disabled={isLoading}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowPhoneLoginModal(true)}
                className="flex items-center gap-1.5 text-xs text-[#8bc43e] font-medium hover:underline transition-colors"
              >
                <Smartphone className="w-3.5 h-3.5" />
                Login with OTP
              </button>
              <Link to="/forgot-password" className="text-xs text-[#8bc43e] font-medium hover:underline">
                Forgot Password?
              </Link>
            </div>
          </div>

          {/* Error Message */}
          {displayError && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
              {displayError}
            </div>
          )}

          {/* Submit Button — green gradient */}
          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-[#8bc43e] to-[#6fa02e] hover:from-[#7ab32e] hover:to-[#5f8a26] text-white rounded-xl font-semibold shadow-lg shadow-[#8bc43e]/25 border-0"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {/* Register Link */}
        <p className="mt-6 text-sm text-muted-foreground text-center">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#8bc43e] font-semibold hover:underline">
            Sign up
          </Link>
        </p>

        {/* Footer */}
        <p className="mt-4 text-xs text-muted-foreground text-center">
          By signing in, you agree to our{' '}
          <a href="https://www.eveebuddy.com/terms_and_conditions.php" target="_blank" rel="noopener noreferrer" className="text-[#8bc43e] underline">Terms of Service</a>
          {' '}and{' '}
          <a href="https://www.eveebuddy.com/privacy_policy.php" target="_blank" rel="noopener noreferrer" className="text-[#8bc43e] underline">Privacy Policy</a>
        </p>
      </motion.div>

      {/* Phone OTP Login Modal */}
      <PhoneLoginModal
        open={showPhoneLoginModal}
        onOpenChange={setShowPhoneLoginModal}
        onSuccess={() => {
          setShowPhoneLoginModal(false);
          navigate(from, { replace: true });
        }}
        onNeedsRegistration={(phone, phoneIdToken) => {
          // Number is SMS-verified but has no account. Send them to signup with the
          // Firebase token so they finish with name + password and no second OTP.
          setShowPhoneLoginModal(false);
          navigate('/register', { state: { phone, phoneIdToken } });
        }}
      />
    </div>
  );
}

export default LoginScreen;
